import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import Button from '../../App/Components/Button/Button'
import { Line } from '../Line/Line'
import Total from '../Total/Total'
import { InvoiceLine } from '../type'

import { ErrorText } from './style'
import style from './style.scss'

type Props = {
  onSave: (lines: InvoiceLine[]) => void
}

export const InvoiceTable = ({ onSave }: Props) => {
  const { t } = useTranslation()
  const [updateLines, setUpdateLines] = useState<InvoiceLine[]>([])

  const [statusFeedback, setStatusFeedback] = useState<string>()

  const handleAddLine = () => {
    const newLines: InvoiceLine[] = [...updateLines]
    let highestId: number = Math.max(
      ...updateLines.map(line => {
        return Number(line.id)
      }),
    )
    if (highestId === -Infinity) highestId = 0

    newLines[newLines.length] = {
      id: `${++highestId}`,
      name: '',
      amount: 1,
      unitPrice: 0,
      discount: 0,
      vat: 21,
      lineTotal: 0,
      unitPriceInclVat: 0,
    }
    setUpdateLines(newLines)
  }

  const updateInvoiceLine = (updatedLine: InvoiceLine, index: number) => {
    const newLines: InvoiceLine[] = [...updateLines]
    newLines[index] = updatedLine
    setUpdateLines(newLines)
  }

  const deleteInvoiceLine = (id: string) => {
    const newLines = [...updateLines].filter(line => {
      return line.id !== id
    })

    setUpdateLines(newLines)
  }

  const userSchema = yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
        amount: yup.number().required().moreThan(-1),
        unitPrice: yup.number().required().moreThan(-1),
        discount: yup.number().required().moreThan(-1).max(100),
        vat: yup.number().moreThan(-1).required(),
        unitPriceInclVat: yup.number().required().moreThan(-1),
        lineTotal: yup.number().required().moreThan(-1),
      }),
    )
    .required()
    .min(1)

  const saveInvoice = async () => {
    const checkSchema = await userSchema.validate(updateLines).catch(error => {
      console.log(error.errors)
      setStatusFeedback(error.errors)
    })
    if (checkSchema) {
      onSave(updateLines)
    }
  }

  return (
    <div>
      <table className={style.table}>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th className={style.textRight}>{t('Number')}</th>
            <th className={style.textRight}>{t('Price excl. VAT')}</th>
            <th className={style.textRight}>{t('Discount')} %</th>
            <th className={style.textRight}>{t('VAT')} %</th>
            <th className={style.textRight}>{t('Price incl. VAT')}</th>
            <th className={style.textRight}>{t('Total')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {updateLines.map((line: InvoiceLine, index: number) => {
            return (
              <Line
                key={line.id}
                lineState={line}
                onUpdate={(updateLine: InvoiceLine) =>
                  updateInvoiceLine(updateLine, index)
                }
                onDelete={() => deleteInvoiceLine(line.id)}
              />
            )
          })}
          <tr>
            <td className={style.textCenter} colSpan={8}>
              <a onClick={handleAddLine}>{t('Add item')}</a>
            </td>
          </tr>
          <Total lines={updateLines} />
        </tbody>
      </table>
      {statusFeedback ? <ErrorText>{statusFeedback}</ErrorText> : null}
      <Button onClick={saveInvoice}>{t('Create invoice')}</Button>
    </div>
  )
}
